import { useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { track } from '@/lib/analytics'
import { emitPollEvent } from '@/api/pollEvents'
import { useChannel } from '@/context/ChannelContext'

/**
 * F-E · Channel picker (n≥2 branch) — юзер пришёл по виральной подписи из webview,
 * привязан к 2+ каналам. B в welcome-handler'e показывает inline_keyboard с magic-link'ом
 * на этот роут; здесь юзер выбирает канал → летим на `/polls/new?channelUUID=&ref=`.
 *
 * Design ref: design_v2_pool/onboarding.html slides 4-5 (adapted for polls-only flow).
 */
export function PollsChannelPickerPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const refPollId = params.get('ref')
  const { channels, selectChannel } = useChannel()

  useEffect(() => {
    track('poll_channel_picker_opened', { ref: refPollId ?? null, channelsCount: channels.length })
    if (refPollId) {
      emitPollEvent('poll_onboarding_opened', {
        pollId: refPollId,
        meta: {
          source: 'lk',
          entryPath: '/polls/channel-picker',
          ref: refPollId,
          channelsCount: channels.length,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refPollId])

  const handlePick = (uuid: string, title: string) => {
    selectChannel(uuid)
    track('poll_channel_picker_selected', { channelUUID: uuid, channelTitle: title, ref: refPollId ?? null })
    // ref пробрасываем дальше — CreatePage эмитит `step_completed` с ним
    const qs = new URLSearchParams({ channelUUID: uuid })
    if (refPollId) qs.set('ref', refPollId)
    navigate(`/polls/new?${qs.toString()}`)
  }

  return (
    <div className="channel-picker">
      <header className="hero">
        <p className="eyebrow">Опросы · шаг 1 из 3</p>
        <h1>
          В каком канале <em>сделать</em> опрос?
        </h1>
        <p className="sub">
          У тебя <b>{channels.length}</b> {channels.length === 1 ? 'канал' : channels.length < 5 ? 'канала' : 'каналов'}.
          Выбери, где опубликуем — потом это можно поменять.
        </p>
      </header>

      {channels.length === 0 && (
        <div className="empty">
          Пока нет подключённых каналов. <Link to="/onboarding/channel">Подключить →</Link>
        </div>
      )}

      <ul className="grid">
        {channels.map((c) => (
          <li key={c.uuid}>
            <button className="card" type="button" onClick={() => handlePick(c.uuid, c.title)}>
              <div className="avatar" style={{ background: gradientFromString(c.uuid) }}>
                {c.title.slice(0, 2).toUpperCase()}
              </div>
              <div className="body">
                <div className="title">{c.title}</div>
                <div className="meta">Канал в MAX</div>
              </div>
              <svg className="chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 6l6 6-6 6" />
              </svg>
            </button>
          </li>
        ))}
      </ul>

      <PageStyles />
    </div>
  )
}

function gradientFromString(s: string): string {
  // Тонкий градиент из hash строки — стабильный, узнаваемый для одного и того же uuid.
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  const hue1 = Math.abs(h) % 360
  const hue2 = (hue1 + 60) % 360
  return `linear-gradient(135deg, hsl(${hue1} 65% 55%), hsl(${hue2} 65% 45%))`
}

function PageStyles() {
  return (
    <style>{`
      .channel-picker { max-width: 720px; margin: 0 auto; padding-bottom: 60px; }
      .channel-picker .hero { margin-bottom: 24px; }
      .channel-picker .eyebrow {
        font-weight: 700; font-size: 11px; letter-spacing: 0.12em;
        text-transform: uppercase; color: var(--tg-mute, #8f9eb0);
        margin: 0 0 10px;
      }
      .channel-picker h1 {
        font-weight: 800; font-size: 32px; line-height: 1.05;
        letter-spacing: -0.024em; color: var(--tg-ink, #111);
        margin: 0 0 8px;
      }
      .channel-picker h1 em { font-style: italic; font-weight: 400; color: var(--tg-blue, #0066ff); }
      .channel-picker .sub { font-size: 14.5px; color: var(--tg-mute, #8f9eb0); margin: 0; line-height: 1.5; }
      .channel-picker .sub b { color: var(--tg-ink-2, #333); font-weight: 600; }

      .channel-picker .grid { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 10px; }
      .channel-picker .card {
        display: grid; grid-template-columns: 44px 1fr 20px; gap: 14px;
        align-items: center; width: 100%;
        padding: 14px 18px;
        background: var(--tg-surface, #fff);
        border: 1px solid var(--tg-line-2, #f0f0f4);
        border-radius: 14px;
        cursor: pointer; font-family: inherit; text-align: left;
        transition: all 160ms;
      }
      .channel-picker .card:hover {
        border-color: var(--tg-blue-soft, rgba(0,128,255,0.4));
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0,0,0,0.06);
      }
      .channel-picker .avatar {
        width: 44px; height: 44px; border-radius: 12px;
        display: flex; align-items: center; justify-content: center;
        color: white; font-weight: 700; font-size: 14px; letter-spacing: 0.04em;
      }
      .channel-picker .title { font-weight: 600; font-size: 15px; color: var(--tg-ink, #111); margin-bottom: 2px; }
      .channel-picker .meta { font-size: 12px; color: var(--tg-mute, #8f9eb0); }
      .channel-picker .chevron { width: 20px; height: 20px; color: var(--tg-mute-2, #c4c4c8); }

      .channel-picker .empty {
        padding: 32px; text-align: center; color: var(--tg-mute, #8f9eb0);
        background: var(--tg-surface, #fff); border: 1px dashed var(--tg-line, #e8e8ec);
        border-radius: 12px; font-size: 14px;
      }
      .channel-picker .empty a { color: var(--tg-blue, #0066ff); text-decoration: none; font-weight: 600; }
    `}</style>
  )
}
